import type { FormFields, FormValues, FormField } from './@types';

/**
 * Type guard to check if a value is a `FormField`
 */
export function isField<T>(value: FormField | T): value is FormField {
    return value != undefined && (value as FormField).value != undefined && (value as FormField).errors != undefined;
}

export function isFieldArray(value: any): value is Array<FormField> {
    return value != undefined && Array.isArray(value) && (value.length === 0 || value.filter((elem) => !isField(elem)).length === 0);
}

export function isPartialField<T>(value: Partial<FormField> | T): value is Partial<FormField> {
    return (
        value !== undefined &&
        ((value as FormField).value != undefined ||
            (value as FormField).errors != undefined ||
            (value as FormField).touched != undefined ||
            (value as FormField).dirty != undefined)
    );
}

export function isString(value: any): value is string {
    return typeof value == 'string' || value instanceof String;
}

/**
 * Traverse a `FormFields` tree and returns `true` if any of the child fields contains
 * errors, otherwise `false`.
 *
 * @param fields - An object representing a tree of fields
 * @returns boolean
 */
export function hasErrors(fields: FormFields): boolean {
    return !!getFirstErrorName(fields);
}

export function getFirstErrorName(fields: FormFields): string | null {
    // eslint-disable-next-line no-restricted-syntax
    for (const name in fields) {
        const field = fields[name];

        if (isField(field)) {
            if (field.errors.length > 0) {
                return field.name || null;
            }
        } else if (isFieldArray(field)) {
            // eslint-disable-next-line no-restricted-syntax
            for (const idx in field) {
                if (field[idx].errors.length > 0) {
                    return field[idx].name || null;
                }
            }
        } else if (field instanceof Object) {
            return getFirstErrorName(field);
        }
    }
    return null;
}

/**
 * Traverse a tree of `FormFields` and replace each `FormField` into its first `string` error value.
 */
export function getErrors(fields: FormFields): FormValues {
    return Object.entries(fields).reduce((values: FormValues, [name, nextField]) => {
        if (isFieldArray(nextField)) {
            const errors = nextField.map((field) => (field.errors.length >= 1 ? field.errors[0] : undefined)).filter(isString);

            if (errors.length >= 1) {
                return {
                    ...values,
                    [name]: errors,
                };
            }
        } else if (isField(nextField)) {
            const error = nextField.errors[0] ? nextField.errors[0] : undefined;

            if (isString(error)) {
                return {
                    ...values,
                    [name]: error,
                };
            }
        } else {
            return {
                ...values,
                [name]: getErrors(nextField),
            };
        }

        return values;
    }, {} as FormValues);
}

/**
 * Traverse a tree of `FormFields` and replace each `FormField` into its `string` value. This
 * returns a new object.
 *
 * @param fields - The object containing the form fields
 */
export function getValues(fields: FormFields): FormValues {
    if (!((fields as any) instanceof Object) || isString(fields)) {
        return {};
    }

    const values = Object.entries(fields).reduce((values: FormValues, [name, nextField]) => {
        if (isFieldArray(nextField)) {
            return {
                ...values,
                [name]: nextField.map((field) => field.value),
            };
        } 
        
        if (isField(nextField)) {
            return {
                ...values,
                [name]: nextField.value,
            };
        } 
        
        if (isString(nextField)) {
            return {
                ...values,
                [name]: nextField,
            };
        } 

        return {
            ...values,
            [name]: getValues(nextField),
        };
        
    }, {});

    return values;
}

export function trimValues(fields: FormValues): FormValues {
    const trimmedFields: FormValues = Object.keys(fields).reduce((fieldsSoFar: FormValues, nextKey: string): FormValues => {
        const val = fields[nextKey];

        if (typeof val == 'string') {
            return {
                ...fieldsSoFar,
                [nextKey]: val.trim(),
            };
        } 
        
        if (Array.isArray(val)) {
            return {
                ...fieldsSoFar,
                [nextKey]: val.map((elem) => {
                    if (typeof elem == 'string') {
                        return elem.trim();
                    }

                    return elem;
                }),
            };
        } 
        
        if (typeof val == 'object' && val) {
            return trimValues(val);
        } 

        return {
            ...fieldsSoFar,
            [nextKey]: val,
        };
        
    }, {} as FormValues);
    return trimmedFields;
}

export function valuesToFields(values: any): FormFields {
    const fields: FormFields = Object.entries(values).reduce((obj, entry) => {
        const [name, value] = entry;

        if (typeof value == 'string' || typeof value == 'number' || typeof value == 'boolean') {
            return {
                ...obj,
                [name]: {
                    value,
                    errors: [],
                },
            };
        } 
        
        if (Array.isArray(value)) {
            return {
                ...obj,
                [name]: value.map((element) => ({
                    value: element,
                    errors: [],
                })),
            };
        } 
        
        if (typeof value == 'undefined' || value == 'null' || typeof value == 'function') {
            return obj;
        } 
        
        return {
            ...obj,
            [name]: valuesToFields(value),
        };
        
    }, {});
    return fields;
}
